import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  NzUploadChangeParam,
  NzUploadFile,
  NzUploadModule,
  NzUploadXHRArgs,
} from 'ng-zorro-antd/upload';
import {
  takeUntil,
  tap,
  switchMap,
  Subject,
  Subscription,
  Observable,
  catchError,
  concatMap,
  EMPTY,
} from 'rxjs';
import { FileSharingApiService } from 'src/app/services/api/file-sharing-api.service';
import { FileSharingService } from 'src/app/services/file-sharing.service';
import { FileSharing } from 'src/app/types/file-sharing.type';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { ActivatedRoute, Router } from '@angular/router';
import { NzFormTooltipIcon, NzFormModule } from 'ng-zorro-antd/form';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzDividerModule } from 'ng-zorro-antd/divider';

export type Steps = {
  title: string;
  hidden?: boolean;
};

type UploadedFile = { name: string; key: string; description: string };

@Component({
  selector: 'app-create-file-sharing',
  templateUrl: './create-file-sharing.component.html',
  styleUrls: ['./create-file-sharing.component.scss'],
  standalone: true,
  imports: [
    NzCardModule,
    NzButtonModule,
    NzUploadModule,
    NzIconModule,
    FormsModule,
    NzInputModule,
    AsyncPipe,
    NzPageHeaderModule,
    NzFormModule,
    NzDividerModule,
  ],
})
export class CreateFileSharingComponent {
  private fileSharingService = inject(FileSharingService);
  private fileSharingApiService = inject(FileSharingApiService);
  private messageService = inject(NzMessageService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  id: number | null = null;
  files: (FileSharing & { new: boolean })[] = [];
  fileList: NzUploadFile[] = [];
  uploadedFiles: UploadedFile[] = [];
  sharingFilesTheme = '';

  addFilesTooltipIcon: NzFormTooltipIcon = {
    type: 'info-circle',
    theme: 'outline',
  };

  pageNumber = this.activatedRoute.snapshot.queryParams['pageNumber'];
  pageSize = this.activatedRoute.snapshot.queryParams['pageSize'];

  actions$?: Observable<any>;

  private uploadQueue = new Subject<NzUploadXHRArgs>();
  private destroy$ = new Subject<void>();

  uploadQueue$ = this.uploadQueue.pipe(
    concatMap((file) => this.handleUpload(file)),
    takeUntil(this.destroy$),
  );

  get disableSendButton(): boolean {
    if (!!this.fileList.length) {
      return !!this.fileList.filter((file) => file.status === 'uploading')
        .length;
    }

    return false;
  }

  sendFileSharing(): void {
    if (!this.id || this.uploadedFiles.length === 0) {
      return;
    }

    const fileDescriptions: { [key: string]: string } = {};
    for (const file of this.uploadedFiles) {
      fileDescriptions[file.key] = file.description;
    }

    const params = {
      id: this.id,
      title: this.sharingFilesTheme,
      fileDescriptions,
    };

    this.actions$ = this.fileSharingService.sendFilesToShare(params).pipe(
      tap(({ ok }) => {
        if (ok) {
          this.messageService.success('Файлы успешно переданы в работу');
          this.router.navigate([`/file-sharing/${this.id}/details`], {
            queryParamsHandling: 'preserve',
          });

          return;
        }

        this.messageService.success('Ошибка отправки');
      }),
    );
  }

  deleteFile(file: UploadedFile): void {
    const index = this.uploadedFiles.indexOf(file);
    if (index > -1) {
      this.uploadedFiles.splice(index, 1);

      this.actions$ = this.fileSharingService.deleteSharedFile({
        id: this.id!,
        contentKey: file.key,
      });
    }
  }

  uploadFile = (file: NzUploadXHRArgs): Subscription => {
    this.uploadQueue.next(file);
    return new Subscription();
  };

  private handleUpload(file: NzUploadXHRArgs) {
    const upload$ = (draftId: number) =>
      this.fileSharingApiService.uploadFileToShare(
        file.file as any,
        String(draftId),
      );

    const createDraftAndUpload$ = this.fileSharingService.createDraft().pipe(
      tap((draftId) => {
        this.id = draftId;
      }),
      switchMap((draftId) => upload$(draftId)),
    );

    const stream$ = this.id ? upload$(this.id) : createDraftAndUpload$;

    return stream$.pipe(
      tap((response) => {
        if (response.progress !== undefined) {
          file.onProgress?.({ percent: response.progress }, file.file);
        }

        if (response.fileKey) {
          const key = response.fileKey;
          const name = key.substring(key.lastIndexOf('/') + 1);

          this.uploadedFiles.push({
            name,
            key,
            description: '',
          });

          file.onSuccess?.(response, file.file, null as any);
        }
      }),
      catchError((error) => {
        file.onError?.(error, file.file);
        this.messageService.error('Ошибка загрузки файла');
        return EMPTY;
      }),
    );
  }

  handleChangeFileList(info: NzUploadChangeParam): void {
    this.fileList = info.fileList.filter((d) => d.status !== 'done');
  }

  goBack(): void {
    this.router.navigate(['/file-sharing'], {
      queryParams: { pageNumber: this.pageNumber, pageSize: this.pageSize },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
